/**
 * AuthGuard
 *
 * Protects routes that require authentication.
 * Redirects to login if user is not authenticated.
 */

import { useEffect } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Spinner } from '../../components/ui/spinner'
import { useAuthStore } from '../../store'

interface AuthGuardProps {
  children: React.ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const isInitialized = useAuthStore((s) => s.isInitialized)

  // Initialize auth state on mount
  useEffect(() => {
    if (!isInitialized) {
      useAuthStore.getState().initialize()
    }
  }, [isInitialized])

  // Show loading spinner while initializing
  if (!isInitialized || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    // Save the attempted location for redirecting after login
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  return <>{children}</>
}
